import { ROW_GROUP_COLUMNS } from 'components/common/AllocationReport/consts';
import { CampaignPlanConfig } from 'components/common/types/CampaignPlan.types';
import { sortByName } from 'utils/sortByName';

export interface ShareCampaignPlanProps {
  isLoading: boolean;
  url: string;
  expiresAt: string;
  error?: string;
  disabledMessage?: string;
}

export const CAMPAIGN_PLAN_LINK_IS_DISABLED_MESSAGE = 'Campaign plan link is disabled';
export const CAMPAIGN_PLAN_CONFIG_LOAD_ERROR =
  'The saved campaign plan configuration could not be loaded. Close and reopen this dialog to try again.';
export const DISABLE_CAMPAIGN_PLAN_LINK_MESSAGE = 'Disable campaign plan access';
export const DISABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION = 'Turning this off will deactivate the campaign plan link.';
export const ENABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION = 'Turning this on will activate the campaign plan link.';
export const CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_MESSAGE = 'Shared campaign link disabled';
export const CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_DESCRIPTION =
  'As you have confirmed changes to the campaign, the shareable link is now disabled. To re-enable please set this in the link settings.';

export interface ToggleCampaignPlanProps {
  isChecked: boolean;
  isDisabled: boolean;
  error?: string;
}

export interface ShareCampaignPlanModalProps {
  onClose: () => void;
  shareCampaignPlan: ShareCampaignPlanProps;
  toggleCampaignPlan: ToggleCampaignPlanProps;
  onToggleSwitchChange: (campaignPlanConfig: CampaignPlanConfig) => void;
  hasSelectedPois: boolean;
  initialCampaignPlanConfig: CampaignPlanConfig;
  isConfigLoading: boolean;
  configLoadError: string;
  onGenerateLink: (campaignPlanConfig: CampaignPlanConfig) => Promise<void>;
  onUpdateLink: (campaignPlanConfig: CampaignPlanConfig, hasConfigChanges: boolean) => Promise<boolean>;
}

export type AllocationMetricColumn = 'planned' | 'delivered';
export type AllocationMetricId = 'sot' | 'repetitions' | 'impressions' | 'grossPrice' | 'indexedPrice';

export interface AllocationMetric {
  id: AllocationMetricId;
  label: string;
}

export const ALLOCATION_METRICS: AllocationMetric[] = [
  { id: 'sot', label: 'Share of time' },
  { id: 'repetitions', label: 'Repetitions' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'grossPrice', label: 'Gross Price' },
  { id: 'indexedPrice', label: 'Indexed Price' },
];

export const ALLOCATION_METRICS_COLUMNS: {
  id: AllocationMetricColumn;
  label: string;
  metrics: AllocationMetric[];
}[] = [
  {
    id: 'planned',
    label: 'Planned',
    metrics: ALLOCATION_METRICS,
  },
  {
    id: 'delivered',
    label: 'Delivered',
    metrics: ALLOCATION_METRICS,
  },
];

export const ALLOCATION_METRICS_COLUMNS_WITHOUT_DELIVERED: {
  id: AllocationMetricColumn;
  label: string;
  metrics: AllocationMetric[];
}[] = [
  {
    id: 'planned',
    label: 'Planned',
    metrics: ALLOCATION_METRICS,
  },
];

export type AllocationReportMetric = Record<AllocationMetricId, { planned: boolean; delivered: boolean }>;

export const clearDeliveredAllocationMetrics = (config: CampaignPlanConfig): CampaignPlanConfig => ({
  ...config,
  allocationReport: ALLOCATION_METRICS.reduce(
    (allocationReport, { id }) => {
      allocationReport[id] = { ...allocationReport[id], delivered: false };
      return allocationReport;
    },
    { ...config.allocationReport },
  ),
});

export type DisabledAllocationMetrics = Record<AllocationMetricColumn, AllocationMetricId[]>;

export const DISABLED_ALLOCATION_METRICS: DisabledAllocationMetrics = {
  planned: ['repetitions'],
  delivered: ['grossPrice', 'indexedPrice'],
};

export const GROUP_BY_OPTIONS = sortByName(ROW_GROUP_COLUMNS);

export const GROUP_BY_OPTION_CODES = GROUP_BY_OPTIONS.map(({ code }) => code);

export interface AllocationMetricCheckboxProps {
  metricId: AllocationMetricId;
  metricLabel: string;
  columnId: AllocationMetricColumn;
  columnLabel: string;
  isSelected: boolean;
  onChange: (isChecked: boolean) => void;
}
